import { useState } from 'react';
import { api } from '../lib/api.js';
import { fechaHora } from '../lib/formato.js';
import { CLS_GUIA, txt, TXT_GUIA, TXT_UBICACION } from '../lib/enums.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Error as AvisoError, Vacio } from '../piezas/Estados.jsx';

interface Serie {
  numero_serie: string;
  numero_parte: string;
  articulo: string;
  ubicacion: string;
  almacen: string | null;
  numero_guia: string | null;
  ticket_folio: string | null;
  sitio_determinante: string | null;
  sitio: string | null;
}

interface Movimiento {
  movimiento_folio: string;
  tipo: string;
  creado_en: string;
  origen: string | null;
  destino: string | null;
  actor: string | null;
}

interface Resultado {
  guias: { folio: string; numero_guia: string; estado: string; origen: string; destino: string }[];
  series: Serie[];
  historial: Movimiento[];
}

/**
 * Rastreo.
 *
 * Una sola busqueda por numero de guia, numero de serie o numero de parte.
 * La pregunta real que contesta es "donde quedo este equipo", asi que la
 * ubicacion de hoy va primero y el historial despues.
 */
export function Rastreo(): JSX.Element {
  const [q, setQ] = useState('');
  const [r, setR] = useState<Resultado | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [buscando, setBuscando] = useState(false);

  async function buscar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault();
    setError(null);
    setBuscando(true);
    try {
      const { datos } = await api.obtener<Resultado>(`/v1/rastreo?q=${encodeURIComponent(q)}`);
      setR(datos);
    } catch (e) {
      setError(e);
      setR(null);
    } finally {
      setBuscando(false);
    }
  }

  const sinResultados = r !== null && r.series.length === 0 && r.guias.length === 0;

  return (
    <>
      <Pagehead />

      <form className="card" onSubmit={(e) => void buscar(e)} style={{ marginBottom: 14 }}>
        <div className="fila wrap">
          <div className="campo crece" style={{ marginBottom: 0 }}>
            <label htmlFor="q">Numero de guia, numero de serie o numero de parte</label>
            <input
              id="q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              minLength={3}
              required
              placeholder="EST987654321, SN-00142 o RTR-SDWAN-100"
              data-prueba="q-rastreo"
            />
          </div>
          <button type="submit" className="btn pri" disabled={buscando} data-prueba="buscar">
            {buscando ? 'Buscando…' : 'Rastrear'}
          </button>
        </div>
      </form>

      {error !== null && <AvisoError error={error} />}

      {sinResultados && (
        <div className="card">
          <Vacio
            mensaje="No se encontro nada con ese dato"
            ayuda="Revisa el numero; la busqueda es exacta, no parcial."
            icono="🔍"
          />
        </div>
      )}

      {r && r.guias.length > 0 && (
        <>
          <div className="sec-title">Guias</div>
          <div className="card p0">
            <table>
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Numero</th>
                  <th>Estado</th>
                  <th>Ruta</th>
                </tr>
              </thead>
              <tbody>
                {r.guias.map((g) => (
                  <tr key={g.folio}>
                    <td className="folio">{g.folio}</td>
                    <td className="folio">{g.numero_guia}</td>
                    <td>
                      <span className={`tag ${CLS_GUIA[g.estado] ?? ''}`}>
                        {txt(TXT_GUIA, g.estado)}
                      </span>
                    </td>
                    <td className="muted">
                      {g.origen} → {g.destino}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {r && r.series.length > 0 && (
        <>
          <div className="sec-title">Donde esta hoy</div>
          <div className="card p0">
            <table data-prueba="tabla-series">
              <thead>
                <tr>
                  <th>Serie</th>
                  <th>Articulo</th>
                  <th>Ubicacion</th>
                  <th>Donde</th>
                </tr>
              </thead>
              <tbody>
                {r.series.map((s) => (
                  <tr key={s.numero_serie}>
                    <td className="folio">{s.numero_serie}</td>
                    <td>
                      {s.articulo}
                      <div className="muted" style={{ fontSize: 11 }}>
                        {s.numero_parte}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`tag ${
                          s.ubicacion === 'UBI_TRANSITO'
                            ? 't-warm'
                            : s.ubicacion === 'UBI_INSTALADA'
                              ? 't-ok'
                              : s.ubicacion === 'UBI_BAJA'
                                ? 't-mute'
                                : 't-cold'
                        }`}
                      >
                        {txt(TXT_UBICACION, s.ubicacion)}
                      </span>
                    </td>
                    <td>
                      {/* Cada ubicacion se explica con lo que la identifica:
                          el almacen, la guia con la que viaja, o el sitio y
                          el ticket con el que se instalo. */}
                      {s.ubicacion === 'UBI_ALMACEN' && s.almacen}
                      {s.ubicacion === 'UBI_TRANSITO' && (
                        <span className="folio">guia {s.numero_guia}</span>
                      )}
                      {s.ubicacion === 'UBI_INSTALADA' && (
                        <>
                          {s.sitio_determinante} · {s.sitio}
                          <div className="folio muted" style={{ fontSize: 11 }}>
                            {s.ticket_folio}
                          </div>
                        </>
                      )}
                      {s.ubicacion === 'UBI_BAJA' && <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {r && r.historial.length > 0 && (
        <>
          <div className="sec-title">Historial de la pieza</div>
          <div className="card">
            <div className="tl">
              {r.historial.map((m) => (
                <div key={m.movimiento_folio} className="tl-it">
                  <div className="fila" style={{ gap: 8 }}>
                    <span className="folio">{m.movimiento_folio}</span>
                    <span className="tag">{m.tipo.replace('MOV_', '')}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 11.5 }}>
                    {m.origen ?? '—'} → {m.destino ?? '—'} · {fechaHora(m.creado_en)}
                    {m.actor && <> · {m.actor}</>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
